import { use100vh } from "react-div-100vh";
import Nav from "@/components/common/Nav";
import PageHead from "@/components/common/PageHead";
import { Button } from "@mantine/core";
import useAccount from "@/hooks/useAccount";
import { DataIcon, GiftIcon, BuildingIcon } from "@/components/common/icons";
import i18n from "@/services/i18n";
import useInvestInfo from "@/hooks/ido/useInvestInfo";
import { useEffect, useState } from "react";
import InvestmentIframe from "@/components/ido/InvestmentIframe";
import { currentRound, IDO_STAGE, IDOStage } from "@/services/ido";
import UserInvestmentInfoDialog from "@/components/ido/UserInvestmentInfoDialog";
import { useDisclosure } from "@mantine/hooks";

const IDO_CAMPAIGN_ID = process.env.NEXT_PUBLIC_IDO_CAMPAIGN_ID as string;
const IDO_CAMPAIGN_SRC = process.env.NEXT_PUBLIC_IDO_CAMPAIGN_SRC as string;

const IDOPage = () => {
  const height = use100vh();
  const { login, isLogin, address } = useAccount();
  const investInfo = useInvestInfo();
  const locale = investInfo.locale;
  const [investMode, setInvestMode] = useState(false);
  const [
    investDialogOpened,
    { open: openInvestDialog, close: closeInvestDialog },
  ] = useDisclosure(false);
  const benefits = [
    {
      Icon: DataIcon,
      name: i18n.t("benefits.data_and_content_access", locale),
      desc: i18n.t("benefits.data_and_content_access_description", locale),
    },
    {
      Icon: GiftIcon,
      name: i18n.t("benefits.staking_and_rewards", locale),
      desc: i18n.t("benefits.staking_and_rewards_description", locale),
    },
    {
      Icon: BuildingIcon,
      name: i18n.t("benefits.governance", locale),
      desc: i18n.t("benefits.governance_description", locale),
    },
  ];

  const introInfo = [
    {
      name: i18n.t("token_distribution.title", locale),
      items: [
        i18n.t("token_distribution.desc1", locale),
        i18n.t("token_distribution.desc2", locale),
        i18n.t("token_distribution.desc3", locale),
      ],
    },
    {
      name: i18n.t("investment_limit.title", locale),
      items: [
        i18n.t("investment_limit.desc1", locale),
        i18n.t("investment_limit.desc2", locale),
      ],
      tip: i18n.t("investment_limit.tip", locale),
    },
  ];

  const cardClass =
    "rounded-lg bg-[linear-gradient(180deg,rgba(255,255,255,0.50)_0%,rgba(255,255,255,0.00)_100%)] py-7 w-[250px] flex flex-col items-center";

  useEffect(() => {
    if (
      IDO_STAGE === IDOStage.ROUND_1_END ||
      IDO_STAGE === IDOStage.ROUND_2_END
    ) {
      setInvestMode(true);
    }
  }, [IDO_STAGE]);

  return (
    <div
      style={{
        height: height ? `${height}px` : "100vh",
      }}
      id="container"
      className="w-screen overflow-x-hidden overflow-y-scroll font-exo text-base bg-[url('/assets/ido/bg1.png')] bg-cover bg-no-repeat"
    >
      <PageHead></PageHead>
      <Nav></Nav>
      <div className="bg-idoBg bg-no-repeat bg-cover -mt-[100px]  flex items-center justify-center pt-[180px] pb-32 sm:pt-[267px] sm:pb-[103px]">
        {!investMode && (
          <div className="flex flex-col items-center">
            <div
              className="py-2 px-4 flex items-center justify-center text-sm font-semibold rounded-lg "
              style={{
                background:
                  "linear-gradient(278.55deg, #FFFFFF -6.14%, #87B5FF 93.7%, #3C86FB 157.68%)",
              }}
            >
              {currentRound === 1 ? "First Round" : "Second Round"}
            </div>
            <div className="mt-8 text-center text-6xl sm:text-[100px] font-extrabold text-dark1 leading-none">
              <div>{i18n.t("initial", locale)}</div>
              <div className="text-primary">{i18n.t("dex", locale)}</div>
              <div>{i18n.t("offering", locale)}</div>
            </div>
            <div className="flex justify-center gap-6 mt-8 flex-wrap">
              <div className={cardClass}>
                <div className="text-[#639EFC] text-[28px] font-extrabold">
                  {currentRound === 1 ? "$0.5 Million" : " $3.5 Million"}
                </div>
                <div className="mt-2 font-medium text-xl text-gray1">
                  {i18n.t("target_goal", locale)}
                </div>
              </div>
              <div className={cardClass}>
                <div className="text-[#639EFC] text-[28px] font-extrabold">
                  {currentRound === 1 ? "$0.03" : "$0.035"}
                </div>
                <div className="mt-2 font-medium text-xl text-gray1">
                  {i18n.t("price", locale)}
                </div>
              </div>
            </div>
            <div className="mt-8 text-center font-medium">
              {currentRound === 1 ? "July 19 - July 21" : "July 22 - August 19"}
            </div>
            <div className="mt-10 flex flex-col gap-2">
              <Button
                className="w-[242px]"
                radius="xl"
                size="xl"
                onClick={() => {
                  setInvestMode(true);
                }}
              >
                Join now
              </Button>
              <Button
                className="w-[242px] bg-white h-[56px] text-sm"
                radius="xl"
                variant="outline"
                onClick={openInvestDialog}
              >
                View my participation
              </Button>
            </div>
          </div>
        )}
        {investMode && (
          <InvestmentIframe
            investInfo={investInfo}
            campaignId={IDO_CAMPAIGN_ID as string}
            campaignSrc={IDO_CAMPAIGN_SRC as string}
          ></InvestmentIframe>
        )}
      </div>
      <div className="relative before:opacity-40 before:bg-blend before:bg-cover before:content-[''] before:absolute before:-top-20 before:left-0 before:w-full before:h-full pt-14 sm:pt-[140px]">
        <div className="flex flex-col items-center relative z-10">
          <div
            className="selection:text-fill-white text-[32px] font-extrabold w-[95%] sm:w-[80%] max-w-[615px] text-center sm:text-5xl !leading-tight"
            style={{
              background:
                "linear-gradient(94.94deg, #0E0909 5.6%, #000000 5.61%, #676767 86.73%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {i18n.t("benefits.staking_benefits", locale)}
          </div>
          <div className="flex gap-4 mt-11 flex-wrap justify-center px-5 sm:px-0">
            {benefits.map((item, index) => {
              return (
                <div
                  key={index}
                  className="sm:w-[362px] bg-tertiary rounded-2xl px-8 py-14"
                >
                  <item.Icon className="w-12 h-12 fill-primary"></item.Icon>
                  <div className="mt-[70px] text-white text-[32px] font-extrabold leading-[1.1]">
                    {item.name}
                  </div>
                  <div className="text-white font-medium text-lg mt-4">
                    {item.desc}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex flex-wrap justify-center gap-[78px] mt-20 sm:mt-[163px] relative z-10 px-5 sm:px-0">
          <img
            src="/assets/ido/star2.png"
            className="absolute top-[-104px] left-[102px] hidden sm:block"
          ></img>
          {introInfo.map((item, index) => {
            return (
              <div key={index} className="sm:w-[445px]">
                <div className="text-[32px] font-extrabold text-gray1 text-center">
                  {item.name}
                </div>
                <div className="mt-8 flex flex-col gap-4 font-medium">
                  {item.items.map((item, index) => {
                    return (
                      <div key={index} className="flex">
                        <div className="mr-1">・</div>
                        <div>{item}</div>
                      </div>
                    );
                  })}
                </div>
                {item.tip && (
                  <div className="text-sm mt-8 font-medium text-gray1">
                    {item.tip}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <img className="mt-10" src="/assets/ido/rocket.png" width={220} />
        <img
          src="/assets/ido/radial.png"
          className="absolute bottom-[315px] right-[77px]"
        ></img>
        <img
          src="/assets/ido/start1.png"
          className="absolute bottom-[61px] right-[398px]"
        ></img>
      </div>
      <UserInvestmentInfoDialog
        opened={investDialogOpened}
        close={closeInvestDialog}
        campaignId={IDO_CAMPAIGN_ID}
      ></UserInvestmentInfoDialog>
    </div>
  );
};

export default IDOPage;
